import { useCallback, useEffect, useState } from 'react';

/**
 * PWA install-prompt logic (OMN-370).
 *
 * OMN-344 shipped the manifest / service worker / icons, so the app is
 * installable — but there was no `beforeinstallprompt` handling, so users had to
 * find the browser's install menu themselves. This hook captures that event
 * (Android / desktop Chromium) and exposes a way to trigger the native prompt,
 * plus an iOS-Safari path (which has no such event — installation is manual via
 * Share → Add to Home Screen).
 *
 * Nothing shows once the app is already installed, or once the user dismisses it.
 */

const DISMISS_KEY = 'omniflyer_pwa_install_dismissed';

/** The non-standard event fired by Chromium browsers before showing the mini-infobar. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isIOSSafari(): boolean {
  const ua = window.navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const webkit = /WebKit/.test(ua);
  const notOtherBrowser = !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return iOS && webkit && notOtherBrowser;
}

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export interface PwaInstall {
  /** Show the banner. */
  visible: boolean;
  /** iOS has no programmatic prompt — the UI shows Add-to-Home-Screen instructions instead. */
  isIOS: boolean;
  /** Trigger the native install prompt (Chromium). No-op on iOS. */
  install: () => Promise<void>;
  /** Dismiss and remember, so it does not nag again. */
  dismiss: () => void;
}

export function usePwaInstall(): PwaInstall {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasDismissed()) {
      setDismissed(true);
      return;
    }

    const onBeforeInstallPrompt = (e: Event) => {
      e.preventDefault(); // stop the default mini-infobar; we drive our own UI
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setDeferred(null);
      setDismissed(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);

    // iOS never fires beforeinstallprompt — offer manual instructions instead.
    if (isIOSSafari()) setIsIOS(true);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* storage unavailable — dismissal just won't persist */
    }
    setDismissed(true);
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice; // 'accepted' → appinstalled fires; 'dismissed' → keep as-is
    setDeferred(null);
  }, [deferred]);

  const visible = !dismissed && (deferred !== null || isIOS);

  return { visible, isIOS, install, dismiss };
}
